<template>
  <Can action="view" subject="thread">
    <div v-if="isDeal" data-component-group="threads-dialog" data-component="edit-deal">
      <dialog-base
        content-class="edit-deal-dialog"
        :max-width="800"
        is-fullscreen
        :is-on="true"
        :on-close="onDealDialogClose"
      >
        <template v-slot:content>
          <div class="dialog-close">
            <v-btn
              icon
              class="trigger close-dialog elevation-0"
              @click="onCancel"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </div>

          <forms-threads-edit-deal
            :is-loading="isEditingThread"
            :on-done="onDone"
            :on-submit="updateThread"
            :is-on="true"
            :on-update-thread="onUpdateThread"
            :on-assign-thread="() => {}"
          />
        </template>
      </dialog-base>
    </div>

    <div v-else data-component-group="threads-dialog" data-component="edit-campaign">
      <dialog-base
        content-class="edit-campaign-dialog"
        :max-width="800"
        is-fullscreen
        :is-on="true"
        :on-close="onCampaignDialogClose"
      >
        <template v-slot:content>
          <div class="dialog-close">
            <v-btn
              icon
              class="trigger close-dialog elevation-0"
              @click="onCancel"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </div>

          <forms-threads-edit-campaign
            :is-loading="isEditingThread"
            :is-on="true"
            :on-done="onDone"
            :on-submit="updateCampaign"
            :on-update-thread="onUpdateThread"
          />
        </template>
      </dialog-base>
    </div>
  </Can>
</template>

<script>
  const { confirm } = window;

  export default {
    emits: ['saved'],
    data() {
      return {
        isEditingThread: false,
      };
    },
    created() {
      if (this.activeThread?.id !== Number(this.$route.params.id)) {
        this.$store.dispatch('threads/getItem', { id: this.$route.params.id })
          .then((thread) => this.$store.dispatch('threads/setActiveItem', thread));
      }
    },
    watch: {
      '$route.params.id'(newId) {
        this.$store.dispatch('threads/getItem', { id: newId })
          .then((thread) => this.$store.dispatch('threads/setActiveItem', thread));
      },
    },
    computed: {
      activeThread() {
        return this.$store.getters['threads/activeItem'];
      },
      isDeal() {
        return this.activeThread?.type?.name?.toLowerCase() === 'deal';
      },
      returnRoute() {
        const isArchived = this.$route.matched.some((r) => r.name === 'archived-threads');
        return isArchived ? 'archived-threads' : 'threads';
      },
      pendingComment() {
        return this.activeThread?.pendingComment;
      },
      hasPendingComment() {
        return this.pendingComment !== '';
      },
      isDirtyDeal() {
        return this.$store.getters['threads/isDirtyDeal'];
      },
      isDirtyCampaign() {
        return this.$store.getters['threads/isDirtyCampaign'];
      },
    },
    methods: {
      // No refetch: it would replace the listing's threads and collapse the
      // comments the user had expanded.
      onCancel() {
        this.$router.push({ name: this.returnRoute, query: this.$route.query });
      },
      // Deny/archive/reopen/publish changed the thread, and the store only
      // refreshes activeItem, so the listing must refetch.
      onDone() {
        this.$emit('saved');
        this.$router.push({ name: this.returnRoute, query: this.$route.query });
      },
      onUpdateThread(thread) {
        if (thread && typeof thread === 'object') {
          this.$store.dispatch('threads/setActiveItem', { ...thread });
        }
        return Promise.resolve(thread);
      },
      onDealDialogClose() {
        if (!this.hasPendingComment && !this.isDirtyDeal) {
          return this.closeDealDialog();
        }

        if (confirm(this.$t('are_you_sure_you_want_to_leave_this_page'))) {
          this.closeDealDialog();
        }
      },
      closeDealDialog() {
        const nextActiveThread = {
          ...this.activeThread,
          pendingComment: '',
          duplicates: [],
        };

        this.onCancel();
        this.$store.dispatch('threads/setDirtyDeal', false);
        return this.$store.dispatch('threads/setActiveItem', nextActiveThread);
      },
      onCampaignDialogClose() {
        if (!this.hasPendingComment && !this.isDirtyCampaign) {
          return this.closeCampaignDialog();
        }

        if (confirm(this.$t('are_you_sure_you_want_to_leave_this_page'))) {
          this.closeCampaignDialog();
        }
      },
      closeCampaignDialog() {
        const nextActiveThread = {
          ...this.activeThread,
          pendingComment: '',
          duplicates: [],
        };

        this.onCancel();
        this.$store.dispatch('threads/setDirtyCampaign', false);
        return this.$store.dispatch('threads/setActiveItem', nextActiveThread);
      },
      updateThread(thread) {
        const query = {
          ...thread,
          id: this.activeThread.id,
        };

        this.isEditingThread = true;

        return this.$store
          .dispatch('threads/updateDeal', query)
          .then((res) => {
            this.$emit('saved');
            this.$router.push({ name: this.returnRoute, query: this.$route.query });
            return res;
          })
          .catch((error) => error)
          .finally(() => {
            this.isEditingThread = false;
          });
      },
      updateCampaign(thread) {
        const query = {
          ...thread,
          id: this.activeThread.id,
        };

        this.isEditingThread = true;

        return this.$store
          .dispatch('threads/updateCampaign', query)
          .then((res) => {
            this.$emit('saved');
            this.$router.push({ name: this.returnRoute, query: this.$route.query });
            return res;
          })
          .catch((error) => error)
          .finally(() => {
            this.isEditingThread = false;
          });
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

.edit-deal-dialog > .modal__backdrop > .modal__content,
.edit-campaign-dialog > .modal__backdrop > .modal__content {
  width: 640px;

  > .modal__body {
    position: relative;

    .dialog-close {
      position: absolute;
      top: $spacing-sm;
      right: 6px;
      width: auto;
      z-index: 10;
      padding: 0;

      .trigger.close-dialog {
        width: 60px;
        min-width: auto;
        height: 60px;
        border-radius: 50%;
        padding: 0;
        margin: 0;
        background-color: transparent;

        .v-icon {
          margin: 0;
          font-size: $text-size-xl + 0.5rem;
          color: map.get($colors, shades, 'white');
        }

        &:before {
          background-color: transparent;
        }
      }
    }

    [data-component='edit-deal'] {
      padding: 0;
    }

    [data-component='deal-hero'] {
      .thread-details {
        position: sticky;
        top: 0;
        width: 100%;
        z-index: 0;
        margin: 0 0 0;
      }
    }
  }
}
</style>
